import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

const ConfirmCtx = createContext(null);

let _confirm = null;

// Imperative variant — usable outside React components / without the hook.
export function confirmDialog(opts) {
  if (_confirm) return _confirm(opts);
  return Promise.resolve(window.confirm(typeof opts === "string" ? opts : opts?.description || ""));
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, opts: {} });
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setState({ open: true, opts: typeof opts === "string" ? { description: opts } : opts });
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  useEffect(() => {
    _confirm = confirm;
    return () => { if (_confirm === confirm) _confirm = null; };
  }, [confirm]);

  const close = (val) => {
    setState((s) => ({ ...s, open: false }));
    if (resolver.current) { resolver.current(val); resolver.current = null; }
  };

  const o = state.opts || {};
  const danger = !!o.danger;

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Dialog open={state.open} onOpenChange={(v) => !v && close(false)}>
        <DialogContent className="bg-[#111520] border-[#1E293B] text-slate-100 max-w-md" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              {danger && <AlertTriangle className="w-5 h-5 text-amber-400" />}
              {o.title || "Please confirm"}
            </DialogTitle>
          </DialogHeader>
          {o.description && <DialogDescription className="text-sm text-slate-300 whitespace-pre-line">{o.description}</DialogDescription>}
          <DialogFooter>
            <Button variant="ghost" onClick={() => close(false)} className="text-slate-400" data-testid="confirm-cancel">
              {o.cancelText || "Cancel"}
            </Button>
            <Button onClick={() => close(true)} data-testid="confirm-ok"
              className={danger ? "bg-red-500 text-white hover:bg-red-600" : "bg-[#7DD3FC] text-[#0A0D14] hover:bg-[#38BDF8]"}>
              {o.confirmText || "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmCtx.Provider>
  );
}
